import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
// @ts-ignore
// @ts-ignore
import { getVocabulary, listGroups, searchContent } from '@plone/volto/actions';
import BigNews from './BigNews';
import SmallNews from './SmallNews';
// eslint-disable-next-line import/no-unresolved
import { NewsItem } from './types';
import CompactFilter from '../../CompactFilter';
import { Batching } from '../../Search/List';
// @ts-ignore
import jwtDecode from 'jwt-decode';
import locationSVG from '../../../../icons/location.svg';
import dateSVG from '../../../../icons/date.svg';
import viewsSVG from '../../../../icons/views.svg';
import headlineSVG from '../../../../icons/verifiziert.svg';
import departmentsSVG from '../../../../icons/fachbereich.svg';
import CompactFilterObject from '../../CompactFilter/CompactFilterObject';
import { AnyAction } from 'redux';
import { RootState } from '../../../../store';

interface JwtPayload {
  sub: string;
}

const NewsContainerView: FC = () => {
  const dispatch = useDispatch();

  const { newsItems, newsTotal, newsLoading, departments, locations, userId } =
    useSelector((state: RootState) => {
      return {
        // @ts-ignore
        newsItems: state?.search?.subrequests?.news?.items,
        // @ts-ignore
        newsTotal: state?.search?.subrequests?.news?.total,
        // @ts-ignore
        newsLoading: state?.search?.subrequests?.news?.loading,
        // @ts-ignore
        departments: state.groups?.groups,
        // @ts-ignore
        locations:
          state.vocabularies?.[
            'hornundhorn.contenttypes.vocabularies.metadata_location'
          ]?.items,
        // @ts-ignore
        userId: state.userSession?.token
          ? jwtDecode<JwtPayload>(state.userSession.token).sub
          : '',
      };
    });

  const BATCH_SIZE = 10;
  const [displayedItems, setDisplayedItems] = useState<NewsItem[]>([]);
  const [openDialog, setOpenDialog] = useState<number>(-1);
  const pageRef = useRef<number>(1);

  const [filter, setFilter] = useState<CompactFilterObject[]>([
    new CompactFilterObject(
      'Datum',
      'select',
      () => true,
      dateSVG,
      [],
      [],
      (active) => {
        const hours = Math.max(...active.map(Number));
        const since = new Date(Date.now() - hours * 3600000);
        return { 'created.query': since.toISOString(), 'created.range': 'min' };
      },
    ),
    new CompactFilterObject(
      'Ungelesen',
      'switch',
      () => true,
      viewsSVG,
      [],
      undefined,
      (_active, ctx) => ({ 'visited_users.not': ctx?.userId }),
    ),
    new CompactFilterObject(
      'Schlagzeile',
      'switch',
      () => true,
      headlineSVG,
      [],
      undefined,
      () => ({ headline: true }),
    ),
    new CompactFilterObject(
      'Fachbereich',
      'select',
      () => true,
      departmentsSVG,
      [],
      [],
      (active) => ({ departments: active }),
    ),
    new CompactFilterObject(
      'Standort',
      'select',
      () => true,
      locationSVG,
      [],
      [],
      (active) => ({ location: active }),
    ),
  ]);

  const filterQuery = useMemo(() => {
    const ctx = { userId };
    return filter.reduce(
      (query, f) => {
        if (f.active.length > 0 && f.toQuery) {
          return { ...query, ...f.toQuery(f.active, ctx) };
        }
        return query;
      },
      {} as Record<string, any>,
    );
  }, [filter, userId]);

  const hasMore = displayedItems.length < (newsTotal ?? 0);

  const locationMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (locations) {
      locations.forEach((loc: { label: string; value: string }) => {
        map[loc.value] = loc.label;
      });
    }
    return map;
  }, [locations]);

  const searchQuery = {
    'path.depth': 1,
    portal_type: ['News Item'],
    exclude_from_nav: false,
    metadata_fields: [
      'created',
      'headline',
      'visited_users',
      'location',
      'image_scales',
    ],
    sort_on: 'created',
    sort_order: 'reverse',
  };

  const dispatchSearch = useCallback(
    (page: number) => {
      dispatch(
        searchContent(
          '/neuigkeiten',
          {
            ...searchQuery,
            ...filterQuery,
            b_size: BATCH_SIZE,
            b_start: (page - 1) * BATCH_SIZE,
          },
          'news',
        ) as AnyAction,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterQuery],
  );

  useEffect(() => {
    dispatch(listGroups() as AnyAction);
    dispatch(
      getVocabulary({
        vocabNameOrURL:
          'hornundhorn.contenttypes.vocabularies.metadata_location',
        size: 1000,
      }) as AnyAction,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterOptions = useMemo<
    Record<string, { label: string; value: string }[]>
  >(
    () => ({
      Datum: [
        { label: 'Letzte Stunde', value: '1' },
        { label: 'Letzte 24 Stunden', value: '24' },
        { label: 'Letzte Woche', value: '168' },
        { label: 'Letzter Monat', value: '730' },
        { label: 'Letztes Quartal', value: '2190' },
        { label: 'Letztes Jahr', value: '8760' },
      ],
      Fachbereich:
        departments
          ?.filter((item: { id: string }) => item.id.includes('department_'))
          .map((item: { title: string; id: string }) => ({
            label: item.title,
            value: item.id,
          })) ?? [],
      Standort: (locations ?? []).map(
        (loc: { label: string; value: string }) => ({
          label: loc.label,
          value: loc.label,
        }),
      ),
    }),
    [departments, locations],
  );

  const filtersWithOptions = useMemo(
    () => filter.map((f) => ({ ...f, all: filterOptions[f.title] ?? f.all })),
    [filter, filterOptions],
  );

  // When filters or query change: reset and re-fetch
  useEffect(() => {
    setDisplayedItems([]);
    pageRef.current = 1;
    dispatchSearch(1);
  }, [dispatchSearch]);

  // Transform catalog metadata into NewsItem shape
  const transformItems = useCallback(
    (items: any[]): NewsItem[] =>
      items.map((item) => ({
        ...item,
        location: (item.location ?? []).map((token: string) => ({
          title: locationMap[token] ?? token,
          token,
        })),
        image: item.image_scales?.image ? item.image_scales.image[0] : null,
        visited_users: item.visited_users ?? [],
        headline: item.headline ?? false,
      })),
    [locationMap],
  );

  // Accumulate items when newsItems changes
  useEffect(() => {
    if (newsItems !== undefined && !newsLoading) {
      const transformed = transformItems(newsItems);
      if (pageRef.current === 1) {
        setDisplayedItems(transformed);
      } else {
        setDisplayedItems((prev) => [...prev, ...transformed]);
      }
    }
  }, [newsItems, newsLoading, transformItems]);

  const addBatchSize = useCallback(() => {
    if (!newsLoading) {
      pageRef.current += 1;
      dispatchSearch(pageRef.current);
    }
  }, [newsLoading, dispatchSearch]);

  const loadAll = useCallback(() => {
    if (!newsLoading) {
      pageRef.current = 1;
      setDisplayedItems([]);
      dispatch(
        searchContent(
          '/neuigkeiten',
          {
            ...searchQuery,
            ...filterQuery,
            b_size: newsTotal ?? 1000,
            b_start: 0,
          },
          'news',
        ) as AnyAction,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsLoading, filterQuery, newsTotal]);

  const wasNewsRead = (item: NewsItem): boolean => {
    return item.visited_users.includes(userId);
  };

  return (
    <>
      <div id="page-document" className={'newscontainer'}>
        <CompactFilter
          openDialog={openDialog}
          setOpenDialog={setOpenDialog}
          filter={filtersWithOptions}
          updateFilter={(updated: CompactFilterObject) =>
            setFilter((prev) =>
              prev.map((f) =>
                updated.title === f.title
                  ? { ...f, active: updated.active }
                  : f,
              ),
            )
          }
        />
        {displayedItems.map((item, idx) => {
          if (item.headline)
            return (
              <BigNews
                title={item.title}
                image={item.image}
                id={item['@id']}
                visited_users={item.visited_users}
                description={item.description}
                created={item.created}
                location={item.location}
                review_state={item.review_state}
                onMouseEnter={() => setOpenDialog(-1)}
                readed={wasNewsRead(item)}
                key={idx}
              />
            );
          return (
            <SmallNews
              title={item.title}
              image={item.image}
              id={item['@id']}
              visited_users={item.visited_users}
              description={item.description}
              created={item.created}
              location={item.location}
              review_state={item.review_state}
              onMouseEnter={() => setOpenDialog(-1)}
              readed={wasNewsRead(item)}
              key={idx}
            />
          );
        })}
        {hasMore && !newsLoading && (
          <Batching
            batchSize={BATCH_SIZE}
            addBatchSize={addBatchSize}
            loadAll={loadAll}
          />
        )}
        {newsLoading && <Loader active inline="centered" />}
      </div>
    </>
  );
};
export default NewsContainerView;
